"use client";
import FastForwardIcon from "@mui/icons-material/FastForward";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RepeatIcon from "@mui/icons-material/Repeat";
import RepeatOnIcon from "@mui/icons-material/RepeatOn";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import ShuffleOnIcon from "@mui/icons-material/ShuffleOn";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import {
  useAudioControls,
  useAudioMiniPlayerState,
  useAudioPrevNextState,
  useAudioSeekbarState,
} from "./useAudio";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";

export const PLAYER_CONTROLS_HEIGHT = 135;

interface PlayerControlsProps {
  onPrev: () => void;
  onNext: () => void;
}

function formatTime(seconds: number): string {
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${String(m).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

export default function PlayerControls({
  onPrev,
  onNext,
}: PlayerControlsProps) {
  const { pause, resume, seek, skip, toggleLoop, toggleShuffle } =
    useAudioControls();
  const { currentTime, duration } = useAudioSeekbarState();
  const { isPlaying, loop, shuffle, playlist } = useAudioMiniPlayerState();
  const { prevSong, nextSong } = useAudioPrevNextState();

  const handlePlayPause = () => {
    if (isPlaying) {
      pause();
    } else {
      resume();
    }
  };

  const handleSeek = (_ev: Event, value: number | number[]) => {
    seek(Array.isArray(value) ? value[0] : value);
  };

  const handleSkip = (seconds: number) => {
    skip(seconds);
  };

  const hasMultiple = playlist.length > 1;

  const PLAY_SIZE = 48;
  const BUTTON_SIZE = 36;

  const playPauseButton = (
    <IconButton
      onClick={handlePlayPause}
      aria-label={isPlaying ? "Oprește" : "Redă"}
      sx={{
        p: 2,
        borderRadius: "50%",
        bgcolor: "common.white",
        color: "common.black",
        "&:hover": { bgcolor: "common.white", opacity: 0.9 },
      }}
    >
      {isPlaying ? (
        <PauseIcon sx={{ fontSize: PLAY_SIZE }} />
      ) : (
        <PlayArrowIcon sx={{ fontSize: PLAY_SIZE }} />
      )}
    </IconButton>
  );

  const prevButton = (
    <IconButton
      aria-label="Anterior"
      onClick={onPrev}
      sx={{
        color: "common.white",
        borderRadius: "50%",
        "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
        pointerEvents: prevSong ? "auto" : "none",
      }}
    >
      <SkipPreviousIcon sx={{ fontSize: BUTTON_SIZE }} />
    </IconButton>
  );
  const nextButton = (
    <IconButton
      aria-label="Următor"
      onClick={onNext}
      sx={{
        color: "common.white",
        borderRadius: "50%",
        "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
        pointerEvents: nextSong ? "auto" : "none",
      }}
    >
      <SkipNextIcon sx={{ fontSize: BUTTON_SIZE }} />
    </IconButton>
  );

  const rewindButton = (
    <IconButton
      aria-label="Înapoi 10s"
      onClick={() => handleSkip(-10)}
      sx={{
        color: "common.white",
        borderRadius: "50%",
        "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
      }}
    >
      <FastRewindIcon sx={{ fontSize: BUTTON_SIZE }} />
    </IconButton>
  );

  const forwardButton = (
    <IconButton
      aria-label="Înainte 10s"
      onClick={() => handleSkip(10)}
      sx={{
        color: "common.white",
        borderRadius: "50%",
        "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
      }}
    >
      <FastForwardIcon sx={{ fontSize: BUTTON_SIZE }} />
    </IconButton>
  );

  const toggleLoopButton = (
    <IconButton
      aria-label={loop ? "Dezactivează repetiția" : "Activează repetiția"}
      onClick={toggleLoop}
      sx={{
        color: "common.white",
        borderRadius: "50%",
        "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
      }}
    >
      {loop ? (
        <RepeatOnIcon sx={{ fontSize: BUTTON_SIZE }} />
      ) : (
        <RepeatIcon sx={{ fontSize: BUTTON_SIZE }} />
      )}
    </IconButton>
  );

  const shuffleButton = (
    <IconButton
      aria-label={shuffle ? "Dezactivează shuffle" : "Activează shuffle"}
      onClick={toggleShuffle}
      sx={{
        color: "common.white",
        borderRadius: "50%",
        "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
      }}
      disabled={!hasMultiple}
    >
      {shuffle ? (
        <ShuffleOnIcon sx={{ fontSize: BUTTON_SIZE }} />
      ) : (
        <ShuffleIcon sx={{ fontSize: BUTTON_SIZE }} />
      )}
    </IconButton>
  );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        height: PLAYER_CONTROLS_HEIGHT,
      }}
    >
      {/* Seek slider */}
      <Slider
        value={currentTime}
        max={duration || 100}
        step={1}
        onChange={handleSeek}
        aria-label="Progres"
        sx={{
          width: "100%",
          color: "common.white",
          "& .MuiSlider-track": { backgroundColor: "common.white" },
          "& .MuiSlider-rail": {
            backgroundColor: "common.white",
            opacity: 0.6,
          },
          "& .MuiSlider-thumb": {
            backgroundColor: "common.white",
            width: 18,
            height: 18,
            boxShadow: "none",
            "&:before": { display: "none" },
          },
        }}
      />

      {/* Time labels */}
      <Box
        sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
      >
        <Typography variant="caption" sx={{ color: "common.white" }}>
          {formatTime(currentTime)}
        </Typography>
        <Typography variant="caption" sx={{ color: "common.white" }}>
          {duration ? formatTime(duration) : "00:00"}
        </Typography>
      </Box>

      {/* Controls row: left (shuffle), center (play controls), right (loop) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box>{shuffleButton}</Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          {hasMultiple ? prevButton : rewindButton}
          {playPauseButton}
          {hasMultiple ? nextButton : forwardButton}
        </Box>

        <Box>{toggleLoopButton}</Box>
      </Box>
    </Box>
  );
}
